import { Component, Input } from '@angular/core';

/**
 * The dashboard's entire icon set used to be raw emoji typed inline in
 * templates — 27+ distinct glyphs with no shared stroke weight, size, or
 * color, and several concepts (success, close, "AI" vs. the brand mark)
 * represented by different glyphs in different places. This centralizes
 * every icon as one hand-drawn line-icon family (20x20 viewBox, 1.6 stroke,
 * round caps/joins) so adding a new icon means adding one @case, not
 * guessing which emoji looks consistent with the rest of the app.
 */
export type IconName =
  | 'chat'
  | 'close'
  | 'check'
  | 'check-circle'
  | 'menu'
  | 'settings'
  | 'sliders'
  | 'home'
  | 'chart-bar'
  | 'file'
  | 'globe'
  | 'paperclip'
  | 'help-circle'
  | 'users'
  | 'user'
  | 'bot'
  | 'log-out'
  | 'edit'
  | 'trash'
  | 'refresh'
  | 'arrow-right'
  | 'arrow-left'
  | 'chevron-down'
  | 'thumbs-up'
  | 'thumbs-down'
  | 'copy'
  | 'play-circle'
  | 'plus'
  | 'shield'
  | 'zap'
  | 'sparkles'
  | 'alert-circle'
  | 'credit-card'
  | 'eye'
  | 'eye-off'
  | 'mail'
  | 'phone'
  | 'download';

@Component({
  selector: 'app-icon',
  standalone: true,
  template: `
    <svg
      [attr.width]="size"
      [attr.height]="size"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="inline-block shrink-0 align-[-0.2em]"
      aria-hidden="true"
    >
      @switch (name) {
        @case ('chat') {
          <path d="M3 6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H8l-4 3v-3H5a2 2 0 0 1-2-2V6z" />
        }
        @case ('close') {
          <path d="M5 5l10 10M15 5L5 15" />
        }
        @case ('check') {
          <path d="M4 10l4 4 8-8" />
        }
        @case ('check-circle') {
          <circle cx="10" cy="10" r="7.5" />
          <path d="M6.8 10.2l2.2 2.2 4.2-4.6" />
        }
        @case ('menu') {
          <path d="M3 5h14M3 10h14M3 15h14" />
        }
        @case ('settings') {
          <circle cx="10" cy="10" r="3" />
          <path
            d="M10 2.5v2.2M10 15.3v2.2M2.5 10h2.2M15.3 10h2.2M4.8 4.8l1.5 1.5M13.7 13.7l1.5 1.5M4.8 15.2l1.5-1.5M13.7 6.3l1.5-1.5"
          />
        }
        @case ('sliders') {
          <path d="M3 6h5M11 6h6" />
          <circle cx="8" cy="6" r="1.6" />
          <path d="M3 10h9M15 10h2" />
          <circle cx="12" cy="10" r="1.6" />
          <path d="M3 14h3M8 14h9" />
          <circle cx="6" cy="14" r="1.6" />
        }
        @case ('home') {
          <rect x="3" y="3" width="6" height="6" rx="1" />
          <rect x="11" y="3" width="6" height="6" rx="1" />
          <rect x="3" y="11" width="6" height="6" rx="1" />
          <rect x="11" y="11" width="6" height="6" rx="1" />
        }
        @case ('chart-bar') {
          <path d="M2.5 17h15M5 17V9.5M10 17V4M15 17v-6" />
        }
        @case ('file') {
          <path d="M4.5 3h7l4 4v10h-11z" />
          <path d="M11.5 3v4h4" />
        }
        @case ('globe') {
          <circle cx="10" cy="10" r="7.5" />
          <path d="M2.5 10h15M10 2.5c2.5 2.5 2.5 12.5 0 15M10 2.5c-2.5 2.5-2.5 12.5 0 15" />
        }
        @case ('paperclip') {
          <path
            d="M13.5 5.5l-7 7a2.5 2.5 0 0 0 3.5 3.5l7.5-7.5a4 4 0 0 0-5.5-5.5l-7.5 7.5a1 1 0 0 0 1.5 1.5l6.5-6.5"
          />
        }
        @case ('help-circle') {
          <circle cx="10" cy="10" r="7.5" />
          <path d="M7.6 7.9a2.4 2.4 0 1 1 3.6 2.1c-1 .6-1.2 1-1.2 2.1" />
          <circle cx="10" cy="14.6" r="0.9" fill="currentColor" stroke="none" />
        }
        @case ('users') {
          <circle cx="7.5" cy="7" r="3" />
          <path d="M2.3 17c0-3 2.3-5.2 5.2-5.2s5.2 2.2 5.2 5.2" />
          <circle cx="15" cy="8" r="2.1" />
          <path d="M13.5 11.7c1.9.4 3.3 2.2 3.4 4.6" />
        }
        @case ('user') {
          <circle cx="10" cy="6.5" r="3" />
          <path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" />
        }
        @case ('bot') {
          <rect x="4" y="6" width="12" height="9" rx="2" />
          <path d="M10 6V3.2" />
          <circle cx="10" cy="2.4" r="1" />
          <circle cx="7.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="12.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
          <path d="M7.5 13h5" />
        }
        @case ('log-out') {
          <path d="M8 3.5H4.3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1H8M12.5 6.5L16.5 10l-4 3.5M6 10h10.3" />
        }
        @case ('edit') {
          <path d="M13 3.5l3.5 3.5L7 17H3.5v-3.5L13 3.5z" />
        }
        @case ('trash') {
          <path d="M4 5.5h12M8 5.5V3.2h4v2.3M6 5.5l1 11.3h6l1-11.3" />
        }
        @case ('refresh') {
          <path d="M16 6.5V3h-3.5" />
          <path d="M16 4.3A6.5 6.5 0 1 1 10 3.5" />
        }
        @case ('download') {
          <path d="M10 3v9M6.5 8.5 10 12l3.5-3.5" />
          <path d="M3.5 13.5V16h13v-2.5" />
        }
        @case ('arrow-right') {
          <path d="M3 10h13M11.5 5l5 5-5 5" />
        }
        @case ('arrow-left') {
          <path d="M17 10H4M8.5 5l-5 5 5 5" />
        }
        @case ('chevron-down') {
          <path d="M5 7.5l5 5 5-5" />
        }
        @case ('thumbs-up') {
          <path d="M4 9h3v8H4z" />
          <path
            d="M7 9l2.5-6a1.5 1.5 0 0 1 2.8 1l-.8 3.3h3a1.7 1.7 0 0 1 1.6 2.3l-1.8 5a1.7 1.7 0 0 1-1.6 1.1H7V9z"
          />
        }
        @case ('thumbs-down') {
          <path d="M4 11h3V3H4z" />
          <path
            d="M7 11l2.5 6a1.5 1.5 0 0 0 2.8-1l-.8-3.3h3a1.7 1.7 0 0 0 1.6-2.3l-1.8-5A1.7 1.7 0 0 0 13.5 2H7v9z"
          />
        }
        @case ('copy') {
          <rect x="7.5" y="7.5" width="8.5" height="8.5" rx="1.5" />
          <path d="M4.5 12.5v-7a1 1 0 0 1 1-1h7" />
        }
        @case ('play-circle') {
          <circle cx="10" cy="10" r="7.5" />
          <path d="M8.3 7l5 3-5 3V7z" fill="currentColor" stroke="none" />
        }
        @case ('plus') {
          <path d="M10 4v12M4 10h12" />
        }
        @case ('shield') {
          <path d="M10 2.5l6 2.2v4.6c0 3.6-2.4 6.4-6 7.9-3.6-1.5-6-4.3-6-7.9V4.7l6-2.2z" />
          <path d="M7.5 9.8l1.8 1.8 3.2-3.4" />
        }
        @case ('zap') {
          <path d="M11 2.5L4.5 11.3h4l-1 6.2 6.5-8.8h-4l1-6.2z" />
        }
        @case ('sparkles') {
          <path d="M6.5 2.5c.4 1.7 1.1 2.4 2.8 2.8-1.7.4-2.4 1.1-2.8 2.8-.4-1.7-1.1-2.4-2.8-2.8 1.7-.4 2.4-1.1 2.8-2.8z" fill="currentColor" stroke="none" />
          <path d="M13.5 6.5c.6 2.4 1.5 3.3 3.9 3.9-2.4.6-3.3 1.5-3.9 3.9-.6-2.4-1.5-3.3-3.9-3.9 2.4-.6 3.3-1.5 3.9-3.9z" />
          <path d="M6 13.5c.3 1.2.8 1.7 2 2-1.2.3-1.7.8-2 2-.3-1.2-.8-1.7-2-2 1.2-.3 1.7-.8 2-2z" fill="currentColor" stroke="none" />
        }
        @case ('alert-circle') {
          <circle cx="10" cy="10" r="7.5" />
          <path d="M10 6.2v4.6" />
          <circle cx="10" cy="13.8" r="0.9" fill="currentColor" stroke="none" />
        }
        @case ('credit-card') {
          <rect x="2.5" y="4.5" width="15" height="11" rx="1.8" />
          <path d="M2.5 8.2h15" />
          <path d="M5 12.2h3.5" />
        }
        @case ('eye') {
          <path d="M2 10c1.8-3.5 4.8-5.5 8-5.5s6.2 2 8 5.5c-1.8 3.5-4.8 5.5-8 5.5S3.8 13.5 2 10z" />
          <circle cx="10" cy="10" r="2.2" />
        }
        @case ('eye-off') {
          <path d="M2 10c1.8-3.5 4.8-5.5 8-5.5 1.4 0 2.7.3 3.9.9M17.4 12.3c-1.7 2-4 3.2-6.4 3.2-3.2 0-6.2-2-8-5.5.7-1.4 1.7-2.6 2.8-3.6" />
          <path d="M8.3 8.3a2.2 2.2 0 0 0 3.1 3.1" />
          <path d="M3 3l14 14" />
        }
        @case ('mail') {
          <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
          <path d="M3 6l7 5 7-5" />
        }
        @case ('phone') {
          <path d="M6.5 3.5h-2a1.5 1.5 0 0 0-1.5 1.6c.4 6 4.9 10.5 10.9 10.9a1.5 1.5 0 0 0 1.6-1.5v-2a1 1 0 0 0-.8-1l-2.4-.5a1 1 0 0 0-1 .4l-.8 1a11.5 11.5 0 0 1-4.4-4.4l1-.8a1 1 0 0 0 .4-1l-.5-2.4a1 1 0 0 0-1-.8z" />
        }
      }
    </svg>
  `,
})
export class IconComponent {
  @Input() name!: IconName;
  @Input() size = 20;
}
